import { DISMISS_OVERLAYS_EVENT } from './DismissOverlaysEvent.js'

/**
 * A single open, top-layer overlay registered with the {@link overlayLayers}
 * stack.
 */
export interface OverlayLayer {
  /** The overlay host element — identity, and a hook for future nesting checks. */
  element: Element
  /** Hide this overlay. Called when another overlay opens. */
  dismiss: () => void
}

/**
 * Position-independent registry of open, top-layer overlays (popouts,
 * dropdowns, comboboxes, tooltips).
 *
 * A **module-level singleton**, deliberately not a DOM-ancestry provider (e.g.
 * Lit context / a `nord-layout` orchestrator). Because membership is JS object
 * identity rather than DOM position, it coordinates overlays wherever they
 * render — teleported to `<body>`, on a page with no layout wrapper, or opened
 * by non-Nord code — which a provider-ancestor approach cannot. This mirrors how
 * MUI (`ModalManager`) and Ark/Zag (`layerStack`) coordinate overlays.
 *
 * The stack owns a single `document` listener for
 * {@link DISMISS_OVERLAYS_EVENT} (`nord-dismiss-overlays`): when any overlay — a
 * Nord modal, or a consumer's own overlay — dispatches it before opening, every
 * registered layer is dismissed. Overlays register on open and unregister on
 * close.
 */
class OverlayLayerStack {
  private layers: OverlayLayer[] = []
  private listening = false

  /**
   * Register an open overlay. Returns a function to call when it closes.
   * Registering starts the shared dismiss listener on first use.
   */
  register(layer: OverlayLayer): () => void {
    this.layers.push(layer)
    this.startListening()

    return () => this.unregister(layer)
  }

  private unregister(layer: OverlayLayer) {
    const index = this.layers.indexOf(layer)
    if (index !== -1) {
      this.layers.splice(index, 1)
    }
  }

  /**
   * Whether `layer` is the most-recently-registered (topmost) open overlay.
   * Exposed for future topmost-only arbitration (e.g. routing Escape to only
   * the top layer); the current dismiss-on-open behaviour dismisses all layers.
   */
  isTopmost(layer: OverlayLayer): boolean {
    return this.layers[this.layers.length - 1] === layer
  }

  /** Dismiss every registered overlay. Iterates a copy, since `dismiss` unregisters. */
  dismissAll() {
    for (const layer of [...this.layers]) {
      layer.dismiss()
    }
  }

  private startListening() {
    // `document` is absent during SSR; registration only happens on the client
    // (overlays open in the browser), so this guard is belt-and-braces.
    if (this.listening || typeof document === 'undefined') {
      return
    }

    this.listening = true
    document.addEventListener(DISMISS_OVERLAYS_EVENT, this.handleDismissEvent)
  }

  private handleDismissEvent = () => {
    this.dismissAll()
  }
}

/**
 * The shared overlay layer stack. A module-level singleton, so overlays
 * coordinate regardless of where they sit in the DOM.
 */
export const overlayLayers = new OverlayLayerStack()
